import { useState, type FormEvent } from "react";
import { FiEye, FiEyeOff, FiPlus, FiX } from "react-icons/fi";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { AnalyticsFeature } from "@domain/features/analytics/analytics-feature";
import {
  login as loginUser,
  resetPassword as resetUserPassword,
  signUp as registerUser,
} from "@domain/features/auth/auth-feature";
import { UserAnalyticsFeature } from "@domain/features/user-analytics/user-analytics-feature";
import { CoreError, CoreErrorCause } from "@domain/models/core-error";
import pdBuddyIcon from "@presentation/assets/images/pd-buddy-icon.png";
import parkinsonsInsightsLogo from "@presentation/assets/icons/parkinsons-insights-logo-icon.svg";

type AuthMode = "login" | "signup" | "reset";

const AUTH_INPUT_CLASS =
  "w-full rounded-xl border border-border bg-surface py-3 pl-4 pr-4 text-base font-semibold text-black shadow-sm transition-colors placeholder:text-text-muted focus:border-[#0056B3]! focus:outline-none! focus:ring-0! focus-visible:rounded-xl! focus-visible:border-[#0056B3]! focus-visible:outline-none! focus-visible:outline-offset-0! focus-visible:ring-0!";

function readMode(value: string | null): AuthMode {
  if (value === "signup" || value === "reset") {
    return value;
  }
  return "login";
}

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function AuthenticationPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const screenPath = `${location.pathname}${location.search || ""}`;
  const mode = readMode(
    searchParams.get("authMode") ?? searchParams.get("mode"),
  );

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [showEmailInUseGuidance, setShowEmailInUseGuidance] = useState(false);
  const [showLoginRetryGuidance, setShowLoginRetryGuidance] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);

  const isResetMode = mode === "reset";
  const isSignupMode = mode === "signup";

  const setMode = (nextMode: AuthMode) => {
    const next = new URLSearchParams(searchParams);
    next.set("authMode", nextMode);
    next.delete("mode");
    setSearchParams(next, { replace: true });
    setError(null);
    setSuccessMessage(null);
    setEmailError(null);
    setShowEmailInUseGuidance(false);
    setShowLoginRetryGuidance(false);
    setPasswordError(null);
    setTermsError(null);
    setPassword("");
    setIsPasswordVisible(false);
    setHasAcceptedTerms(false);
  };

  const closeModal = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("authMode");
    next.delete("mode");
    setSearchParams(next, { replace: true });
    setError(null);
    setSuccessMessage(null);
    setEmailError(null);
    setShowEmailInUseGuidance(false);
    setShowLoginRetryGuidance(false);
    setPasswordError(null);
    setTermsError(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setEmailError(null);
    setShowEmailInUseGuidance(false);
    setShowLoginRetryGuidance(false);
    setPasswordError(null);
    setTermsError(null);

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setEmailError("Please enter your email.");
      return;
    }

    if (!isValidEmail(normalizedEmail)) {
      setEmailError(
        "Please enter a valid email address (e.g. name@example.com).",
      );
      return;
    }

    if (!isResetMode && password.trim().length === 0) {
      setPasswordError("Please enter your password.");
      return;
    }

    if (isSignupMode && password.trim().length < 6) {
      setPasswordError("Password must be at least 6 characters long.");
      return;
    }

    if (isSignupMode && !hasAcceptedTerms) {
      setTermsError("Please, you need to accept this before continuing.");
      return;
    }

    setIsSubmitting(true);
    try {
      if (isResetMode) {
        await resetUserPassword(normalizedEmail);
        UserAnalyticsFeature.trackAuthResetSuccess(screenPath);
        setSuccessMessage(
          "Password reset email was sent. Check your Spam folder if you do not see it in your inbox.",
        );
      } else if (isSignupMode) {
        await registerUser(normalizedEmail, password);
        UserAnalyticsFeature.trackAuthSignupSuccess(screenPath);
        closeModal();
      } else {
        await loginUser(normalizedEmail, password);
        UserAnalyticsFeature.trackAuthLoginSuccess(screenPath);
        closeModal();
      }
    } catch (submitError) {
      if (submitError instanceof CoreError) {
        switch (submitError.cause) {
          case CoreErrorCause.EmailAlreadyInUse:
            setShowEmailInUseGuidance(true);
            return;
          case CoreErrorCause.DataNotFound:
            setEmailError("No account found for this email address.");
            return;
          case CoreErrorCause.WeakPassword:
            setPasswordError("Please choose a stronger password.");
            return;
          case CoreErrorCause.InvalidCredential:
            if (submitError.message.toLowerCase().includes("email")) {
              setEmailError("Please enter a valid email address.");
              return;
            }

            if (mode === "login") {
              setShowLoginRetryGuidance(true);
              return;
            }

            setPasswordError("Incorrect email or password. Please try again.");
            return;
          case CoreErrorCause.NetworkConnection:
            setError(
              "Network error. Please check your connection and try again.",
            );
            return;
          case CoreErrorCause.Forbidden:
            setError(
              "Too many attempts or operation is currently not allowed.",
            );
            return;
          default:
            break;
        }
      }

      setError(
        submitError instanceof Error
          ? submitError.message
          : "Authentication failed. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-60 bg-black/55">
      <button
        type="button"
        className="absolute inset-0"
        aria-label="Close authentication modal"
        onClick={closeModal}
      />

      <div className="relative z-10 flex h-full w-full items-center justify-center md:p-6">
        <section
          role="dialog"
          aria-modal="true"
          aria-label="Authentication"
          className="relative h-full w-full overflow-y-auto bg-blue text-white md:h-auto md:max-h-[90vh] md:max-w-md md:rounded-2xl md:shadow-2xl"
        >
          <button
            type="button"
            onClick={closeModal}
            aria-label="Close authentication modal"
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-lg text-white transition-colors hover:bg-white/15"
          >
            <FiX className="h-6 w-6" />
          </button>

          <div className="px-6 py-8 md:px-7 md:py-9">
            <div className="mt-2 flex flex-col gap-5">
              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="space-y-5">
                  <div>
                    <h1 className="text-2xl font-bold pb-4">
                      {isResetMode
                        ? "Reset password"
                        : isSignupMode
                          ? "Sign up"
                          : "Login"}
                    </h1>
                  </div>

                  {!isResetMode && (
                    <div className="rounded-xl border border-white/25 bg-white/10 p-3.5">
                      <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                        <div className="inline-flex w-fit rounded-xl bg-white px-3 py-2.5">
                          <img
                            src={parkinsonsInsightsLogo}
                            alt="ParkinsonsInsights"
                            className="h-9 w-auto"
                          />
                        </div>

                        <div
                          aria-hidden="true"
                          className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-white bg-white/15 text-white"
                        >
                          <FiPlus className="h-4 w-4" strokeWidth={3} />
                        </div>

                        <a
                          href="https://pdbuddy.app/"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            AnalyticsFeature.trackExternalLinkOpen(
                              screenPath,
                              "auth_modal",
                              "external_link",
                              "https://pdbuddy.app/",
                            );
                          }}
                          className="inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-xl bg-pdbuddy-background px-3 py-2.5 no-underline transition-colors hover:bg-[#f2e8dc]"
                        >
                          <img
                            src={pdBuddyIcon}
                            alt="PD Buddy"
                            className="h-9 w-9 rounded-md object-cover"
                          />
                          <span className="text-base font-bold text-primary">
                            PD Buddy
                          </span>
                        </a>
                      </div>

                      <p className="mt-3.5 text-center text-sm leading-snug font-semibold text-white sm:text-base">
                        Parkinson&apos;s Insights and PD Buddy work together.
                        You can use the same email and password in both
                        platforms, or sign up with a new email address.
                      </p>
                    </div>
                  )}

                  {isResetMode && (
                    <p className="rounded-lg border border-white/25 bg-white/10 px-3.5 py-3 text-sm leading-relaxed font-semibold text-white/95 sm:text-base">
                      Enter the same email you use for Parkinson’s Insights or
                      the PD Buddy app, and we'll send you a reset link.
                      <br />
                      <br />
                      If you request a reset email here, you will be logged out
                      of the PD Buddy app if you're currently signed in. You can
                      then log back in using your new password.
                    </p>
                  )}

                  <div className="space-y-4 pt-4">
                    <div>
                      <label
                        htmlFor="auth-email"
                        className="mb-1.5 block text-sm font-semibold"
                      >
                        Email
                      </label>
                      <input
                        id="auth-email"
                        type="email"
                        value={email}
                        onChange={(event) => {
                          setEmail(event.target.value);
                          setShowEmailInUseGuidance(false);
                          setShowLoginRetryGuidance(false);
                        }}
                        autoCapitalize="none"
                        autoCorrect="off"
                        inputMode="email"
                        autoComplete="email"
                        placeholder="name@example.com"
                        className={AUTH_INPUT_CLASS}
                        aria-invalid={Boolean(emailError)}
                      />
                      {emailError && (
                        <p className="mt-1.5 text-xs font-semibold text-white/90">
                          {emailError}
                        </p>
                      )}

                      {isSignupMode && showEmailInUseGuidance && (
                        <p className="mt-2 rounded-lg border-2 border-red-300 bg-red-500/20 px-3.5 py-3 text-base leading-relaxed font-extrabold text-red-100 sm:text-lg">
                          It looks like this email is already being used for an
                          account registered here or in the PD Buddy app. Try
                          logging in with the same email and password, or sign
                          up with a different email. If you've forgotten your
                          password, you can{" "}
                          <button
                            type="button"
                            onClick={() => setMode("reset")}
                            className="font-black text-red-100 underline underline-offset-2 transition-colors hover:text-white"
                          >
                            reset it here.
                          </button>
                          .
                        </p>
                      )}

                      {isResetMode && successMessage && (
                        <p className="mt-2 rounded-lg border-2 border-red-300 bg-red-500/20 px-3.5 py-3 text-base leading-relaxed font-extrabold text-red-100 sm:text-lg">
                          Password reset email was sent. Check your Spam folder
                          if you do not see it in your inbox.
                        </p>
                      )}
                    </div>

                    {!isResetMode && (
                      <div>
                        <label
                          htmlFor="auth-password"
                          className="mb-1.5 block text-sm font-semibold"
                        >
                          Password
                        </label>
                        <div className="relative">
                          <input
                            id="auth-password"
                            type={isPasswordVisible ? "text" : "password"}
                            value={password}
                            onChange={(event) => {
                              setPassword(event.target.value);
                              setShowLoginRetryGuidance(false);
                            }}
                            autoComplete={
                              isSignupMode ? "new-password" : "current-password"
                            }
                            placeholder="Enter your password"
                            className={`${AUTH_INPUT_CLASS} pr-12`}
                            aria-invalid={Boolean(passwordError)}
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setIsPasswordVisible((prev) => !prev)
                            }
                            className="absolute right-3 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-primary transition-colors hover:bg-border-light"
                            aria-label={
                              isPasswordVisible
                                ? "Hide password"
                                : "Show password"
                            }
                          >
                            {isPasswordVisible ? (
                              <FiEyeOff className="h-4 w-4" />
                            ) : (
                              <FiEye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                        {passwordError && (
                          <p className="mt-1.5 text-xs font-semibold text-white/90">
                            {passwordError}
                          </p>
                        )}

                        {mode === "login" && showLoginRetryGuidance && (
                          <p className="mt-2 rounded-lg border-2 border-red-300 bg-red-500/20 px-3.5 py-3 text-base leading-relaxed font-extrabold text-red-100 sm:text-lg">
                            We couldn&apos;t log you in with those details.
                            Please try again or{" "}
                            <button
                              type="button"
                              onClick={() => setMode("reset")}
                              className="font-black text-red-100 underline underline-offset-2 transition-colors hover:text-white"
                            >
                              reset your password
                            </button>
                            .
                          </p>
                        )}
                      </div>
                    )}

                    {isSignupMode && (
                      <div>
                        <div
                          className={`flex items-start gap-2.5 rounded-lg px-3 py-2.5 ${
                            termsError
                              ? "border border-red-300 bg-red-500/20"
                              : "border border-white/25 bg-white/10"
                          }`}
                        >
                          <input
                            id="signup-terms"
                            type="checkbox"
                            checked={hasAcceptedTerms}
                            onChange={(event) => {
                              setHasAcceptedTerms(event.target.checked);
                              if (event.target.checked) setTermsError(null);
                            }}
                            className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/60"
                          />
                          <label
                            htmlFor="signup-terms"
                            className="text-sm leading-relaxed text-white/95 sm:text-base"
                          >
                            I have read and agree to the{" "}
                            <Link
                              to="/legal/terms-of-service"
                              className="font-bold text-white underline underline-offset-2"
                            >
                              terms of service
                            </Link>
                            ,{" "}
                            <Link
                              to="/legal/privacy-policy"
                              className="font-bold text-white underline underline-offset-2"
                            >
                              privacy policy
                            </Link>
                            , and{" "}
                            <Link
                              to="/legal/medical-disclaimer"
                              className="font-bold text-white underline underline-offset-2"
                            >
                              medical disclaimer
                            </Link>
                            .
                          </label>
                        </div>
                        {termsError && (
                          <p className="mt-1.5 text-sm font-semibold text-red-100">
                            {termsError}
                          </p>
                        )}
                      </div>
                    )}

                    {mode === "login" && (
                      <button
                        type="button"
                        onClick={() => setMode("reset")}
                        className="w-full text-center text-sm font-semibold text-white underline underline-offset-2 transition-opacity hover:opacity-85"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-5 pt-6">
                  {error && (
                    <p className="rounded-lg border border-white/35 bg-white/10 px-3 py-2 text-sm">
                      {error}
                    </p>
                  )}

                  {successMessage && !isResetMode && (
                    <p className="rounded-lg border border-white/35 bg-white/10 px-3 py-2 text-sm">
                      {successMessage}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full rounded-lg bg-violet px-4 py-3 text-base font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isSubmitting
                      ? "Please wait..."
                      : isResetMode
                        ? "Send reset password"
                        : isSignupMode
                          ? "Sign up"
                          : "Login"}
                  </button>
                </div>
              </form>

              {mode === "signup" ? (
                <div>
                  <button
                    type="button"
                    onClick={() => setMode("login")}
                    className="w-full text-center text-sm font-semibold text-white underline underline-offset-2 transition-opacity hover:opacity-85"
                  >
                    Already have an account? Try to log in
                  </button>
                </div>
              ) : mode === "login" ? (
                <div>
                  <button
                    type="button"
                    onClick={() => setMode("signup")}
                    className="w-full text-center text-sm font-semibold text-white underline underline-offset-2 transition-opacity hover:opacity-85"
                  >
                    Don&apos;t have an account? Sign up
                  </button>
                </div>
              ) : (
                <div>
                  <button
                    type="button"
                    onClick={() => setMode("login")}
                    className="w-full text-center text-sm font-semibold text-white underline underline-offset-2 transition-opacity hover:opacity-85"
                  >
                    Back to login
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
